import {
  Alert,
  SpaceBetween,
} from '@cloudscape-design/components';
import { formatDuration } from 'features/plc/components/common';
import PLCButton from 'features/plc/components/PLCButton';
import {
  HEAT_ZONE_FAULT_REASON,
  HEAT_ZONE_FAULT_REASON_LABELS,
  PLC_PATHS,
} from 'features/plc/contracts/pressHMI.schema';
import { usePLCValue } from 'features/plc/providers/PLCProvider';
import React from 'react';

const formatLimit = (value, suffix) => (
  typeof value === 'number' ? `${value} ${suffix}` : 'the configured limit'
);

const getFaultMessage = ({ faultReason, heatConfig, sideLabel }) => {
  const label = HEAT_ZONE_FAULT_REASON_LABELS[faultReason] || 'Unknown heater fault';

  if (faultReason === HEAT_ZONE_FAULT_REASON.TOOL_RTD_OVER_TEMP) {
    return `${sideLabel}: ${label}. Limit: ${formatLimit(heatConfig.toolTempMax, '°C')}.`;
  }

  if (faultReason === HEAT_ZONE_FAULT_REASON.TOOL_RTD_UNDER_TEMP) {
    return `${sideLabel}: ${label}. Limit: ${formatLimit(heatConfig.toolTempMinValid, '°C')}.`;
  }

  if (faultReason === HEAT_ZONE_FAULT_REASON.TOOL_RTD_SPREAD_TOO_HIGH) {
    return `${sideLabel}: ${label}. Limit: ${formatLimit(heatConfig.toolRtdSpreadMax, '°C')}.`;
  }

  if (faultReason === HEAT_ZONE_FAULT_REASON.HEATER_SAFETY_RTD_OVER_TEMP) {
    return `${sideLabel}: ${label}. Limit: ${formatLimit(heatConfig.heaterTempMax, '°C')}.`;
  }

  if (faultReason === HEAT_ZONE_FAULT_REASON.HEAT_RESPONSE_TOO_LOW) {
    return `${sideLabel}: ${label}. Limit: ${formatLimit(heatConfig.faultTempRateAtFullOutputCpm, '°C/min')}.`;
  }

  return `${sideLabel}: ${label}.`;
};

const useHeatZoneAlertData = (side) => {
  const heatBlock = PLC_PATHS.status[`${side}HeatBlock`];
  const heatConfig = PLC_PATHS.cmd[`${side}HeatZoneConfig`];

  return {
    fault: usePLCValue(heatBlock.fault),
    faultLatched: usePLCValue(heatBlock.faultLatched),
    faultReason: usePLCValue(heatBlock.faultReason),
    heatConfig: {
      faultTempRateAtFullOutputCpm: usePLCValue(heatConfig.faultTempRateAtFullOutputCpm),
      heaterTempMax: usePLCValue(heatConfig.heaterTempMax),
      toolRtdSpreadMax: usePLCValue(heatConfig.toolRtdSpreadMax),
      toolTempMinValid: usePLCValue(heatConfig.toolTempMinValid),
      toolTempMax: usePLCValue(heatConfig.toolTempMax),
    },
    heatResponseFault: usePLCValue(heatBlock.heatResponseFault),
    heatResponseWarning: usePLCValue(heatBlock.heatResponseTooLow),
    warningHeatRateMin: usePLCValue(heatConfig.minTempRateAtFullOutputCpm),
  };
};

const HEAT_AUTO_SHUTDOWN_WARNING_MS = 15 * 60 * 1000;

export default function HeatAlerts() {
  const upper = useHeatZoneAlertData('upper');
  const lower = useHeatZoneAlertData('lower');
  const heatAutoShutdownRemaining = usePLCValue(PLC_PATHS.status.heatAutoShutdownRemaining);
  const hasActiveFault = Boolean(upper.fault || lower.fault);
  const hasFaultLatched = Boolean(upper.faultLatched || lower.faultLatched);
  const canResetHeaterCheck = hasFaultLatched && !hasActiveFault;
  const heatFaultMessages = [
    upper.fault || upper.faultLatched ? getFaultMessage({
      faultReason: upper.faultReason,
      heatConfig: upper.heatConfig,
      sideLabel: 'Upper',
    }) : null,
    lower.fault || lower.faultLatched ? getFaultMessage({
      faultReason: lower.faultReason,
      heatConfig: lower.heatConfig,
      sideLabel: 'Lower',
    }) : null,
  ].filter(Boolean);
  const heatWarningMessages = [
    upper.heatResponseWarning && !upper.heatResponseFault
      ? `Upper: Heating rate is lower than expected while commanded at full output. Warning threshold: ${formatLimit(upper.warningHeatRateMin, '°C/min')}.`
      : null,
    lower.heatResponseWarning && !lower.heatResponseFault
      ? `Lower: Heating rate is lower than expected while commanded at full output. Warning threshold: ${formatLimit(lower.warningHeatRateMin, '°C/min')}.`
      : null,
  ].filter(Boolean);

  return (
    <>
      {heatFaultMessages.length || heatWarningMessages.length || hasFaultLatched ? (
        <Alert
          type={heatFaultMessages.length || hasFaultLatched ? 'error' : 'warning'}
          header={heatFaultMessages.length || hasFaultLatched ? 'Heater safety fault' : 'Heater response warning'}
        >
          <SpaceBetween size="xs">
            {[...heatFaultMessages, ...heatWarningMessages].map((message) => (
              <div key={message}>{message}</div>
            ))}
            {heatFaultMessages.length || hasFaultLatched ? (
              <div>
                The PLC shut affected heater outputs off.
                Reset the heater check after verifying the machine.
              </div>
            ) : (
              <div>
                Run may continue, but check tooling, thermal interface material, heaters,
                sensors, and output hardware before starting the next cycle.
              </div>
            )}
            {hasFaultLatched ? (
              <div>
                <PLCButton
                  path={PLC_PATHS.cmd.resetHeaterCheck}
                  value
                  variant="primary"
                  disabled={!canResetHeaterCheck}
                >
                  Reset heater check
                </PLCButton>
              </div>
            ) : null}
          </SpaceBetween>
        </Alert>
      ) : null}

      {heatAutoShutdownRemaining > 0
        && heatAutoShutdownRemaining <= HEAT_AUTO_SHUTDOWN_WARNING_MS ? (
          <Alert type="warning" header="Heaters will auto-shutdown soon">
            No automation cycles have been run for a while.
            Please run a cycle or the heaters will shut off in
            {' '}
            {formatDuration(heatAutoShutdownRemaining)}
            .
          </Alert>
        ) : null}
    </>
  );
}
